import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "../../store";
import { selectActionLogDetailsModal, closeActionLogDetailsModal } from "../../store/adminSlice";
import { TimestampDisplay } from "../../internal/components/TimestampDisplay";
import JSONString from "./JSONString";

export function ActionLogDetailsModal() {
  const dispatch = useDispatch<AppDispatch>();
  const { open, log } = useSelector(selectActionLogDetailsModal);

  const handleClose = () => {
    dispatch(closeActionLogDetailsModal());
  };

  if (!log) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: "80vh" }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">
            Action Log Details
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Basic Information */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Basic Information
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {log.id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Type
                </Typography>
                <Chip label={log.type} size="small" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Created At
                </Typography>
                <TimestampDisplay timestamp={log.createdAt} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  User
                </Typography>
                <Typography variant="body2">
                  {log.user?.email || "Unknown"}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Memo */}
          {log.memo && (
            <>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Memo
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {log.memo}
                </Typography>
              </Box>
              <Divider />
            </>
          )}

          {/* Description */}
          {log.description && (
            <>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {log.description}
                </Typography>
              </Box>
              <Divider />
            </>
          )}

          {/* Related Email */}
          {log.email && (
            <>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Related Email
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Subject
                    </Typography>
                    <Typography variant="body2">
                      {log.email.subject}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      From
                    </Typography>
                    <Typography variant="body2">
                      {log.email.fromEmail}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Message ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {log.email.messageId}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Divider />
            </>
          )}

          {/* Related IDs */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Related Resources
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              {log.gptRequestId && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    GPT Request ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {log.gptRequestId}
                  </Typography>
                </Box>
              )}
              {log.extractedDataId && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Extracted Data ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {log.extractedDataId}
                  </Typography>
                </Box>
              )}
              {log.derivedDataId && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Derived Data ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {log.derivedDataId}
                  </Typography>
                </Box>
              )}
              {log.workflowId && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Workflow ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {log.workflowId}
                  </Typography>
                </Box>
              )}
              {log.recurringWorkflowId && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Recurring Workflow ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {log.recurringWorkflowId}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Extra Data */}
          {log.extra && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Extra Data
                </Typography>
                <JSONString data={log.extra} />
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}